/**
 * Insert a must-run-first snippet (storage shim, console capture, scrollbar
 * style) at the start of a preview document's <head>.
 *
 * With no `<head>` it must NOT simply prepend: anything but whitespace or
 * comments ahead of `<!doctype html>` makes the parser ignore the doctype and
 * render the page in QUIRKS mode — a different box model than the same file in
 * a normal tab. So the fallbacks are: right after `<html …>` (the parser opens
 * an implicit <head> for it), else right after the doctype, else prepend (a bare
 * fragment has no doctype to protect).
 *
 * Only the document PROLOGUE is scanned — BOM, whitespace, comments, an XML
 * declaration, the doctype, the `<html>` and `<head>` start tags, in order — and
 * the scan stops at the first thing that is none of those. Searching the whole
 * document instead (a) matched a `<head>` inside a body script's JS string and
 * spliced the snippet into the author's code, ending their <script> early, and
 * (b) was a regex that backtracked exponentially on a run of leading comments,
 * freezing the API's event loop on a ~250-byte upload. This is a linear scan
 * with no regex over the document.
 *
 * ⚠️ MUST STAY IMPORT-FREE — bundled into the WEB app via the client-safe
 * `@papan/files/api/head-inject` subpath (HtmlViewer's inline srcDoc) as well as
 * used by the /s/ serve path.
 */

export function injectAtHeadStart(html: string, snippet: string): string {
  const at = headStartOffset(html)
  return html.slice(0, at) + snippet + html.slice(at)
}

/** Offset just past the last of doctype / `<html…>` / `<head…>` in the prologue
 *  (just past a leading BOM when there is none). */
function headStartOffset(html: string): number {
  let i = html.charCodeAt(0) === 0xfeff ? 1 : 0
  let at = i

  // Whitespace and comments may sit anywhere in the prologue. An unterminated
  // comment ends the scan — nothing after it is markup.
  const skipTrivia = (): void => {
    for (;;) {
      while (i < html.length && isSpace(html.charCodeAt(i))) i++
      const end = commentEnd(html, i)
      if (end === -1) return
      i = end
    }
  }

  skipTrivia()
  if (html.slice(i, i + 9).toLowerCase() === '<!doctype') {
    const end = html.indexOf('>', i)
    if (end === -1) return at
    i = at = end + 1
    skipTrivia()
  }
  for (const tag of ['html', 'head']) {
    const end = startTagEnd(html, i, tag)
    if (end === -1) continue
    i = at = end
    skipTrivia()
  }
  return at
}

/**
 * If a comment the HTML tokenizer recognises begins at `i`, the offset just past
 * it; `html.length` if it never closes; otherwise -1. Per the spec a comment is
 * `<!--` closed by `-->` or `--!>`, or abruptly by `<!-->` / `<!--->`; and any
 * other `<!…>` (not a doctype), `<?…>` (an XML declaration) or `</` + a
 * non-letter is a BOGUS comment running to the next `>` (`</>` is dropped). Missing one ends the scan early and lands the snippet
 * before the doctype — quirks mode.
 *
 * Linear: the `--` search resumes past each non-closing `--`, never rescanning.
 */
function commentEnd(html: string, i: number): number {
  if (html.startsWith('<!--', i)) {
    const j = i + 4
    if (html[j] === '>') return j + 1 // <!-->
    if (html.startsWith('->', j)) return j + 2 // <!--->
    for (let k = j; ;) {
      const dd = html.indexOf('--', k)
      if (dd === -1) return html.length
      if (html[dd + 2] === '>') return dd + 3
      if (html[dd + 2] === '!' && html[dd + 3] === '>') return dd + 4
      k = dd + 1
    }
  }
  // `</` + a non-letter: `</>` is dropped outright; anything else (`</ x>`,
  // `</!x>`) is a bogus comment. `</x>` is a real end tag — not trivia.
  if (html.startsWith('</', i)) {
    const c = html.charCodeAt(i + 2)
    if (html[i + 2] === '>') return i + 3
    if (Number.isNaN(c) || (c >= 0x41 && c <= 0x5a) || (c >= 0x61 && c <= 0x7a)) return -1
  }
  const bogus = (html.startsWith('<!', i) && html.slice(i, i + 9).toLowerCase() !== '<!doctype') ||
    html.startsWith('<?', i) || html.startsWith('</', i)
  if (!bogus) return -1
  const end = html.indexOf('>', i + 2)
  return end === -1 ? html.length : end + 1
}

/**
 * If a `<tag …>` start tag begins at `i`, the offset just past its `>`;
 * otherwise -1. Follows the tokenizer's attribute states: a quote opens a value
 * ONLY directly after `name=` (plus whitespace). `<html ="x>` / `<html a=="x>`
 * close at the first `>` — treating those `"`s as quotes would run the scan on
 * into the author's markup.
 */
function startTagEnd(html: string, i: number, tag: string): number {
  const nameEnd = i + 1 + tag.length
  if (html[i] !== '<' || html.slice(i + 1, nameEnd).toLowerCase() !== tag) return -1
  // `<html-widget>` / `<header>` are other elements, not this tag.
  if (nameEnd < html.length && !isSpace(html.charCodeAt(nameEnd)) && html[nameEnd] !== '/' && html[nameEnd] !== '>') return -1

  type State = 'beforeName' | 'name' | 'afterName' | 'beforeValue' | 'unquoted'
  let state: State = 'beforeName'
  for (let j = nameEnd; j < html.length; j++) {
    const c = html[j]
    const space = isSpace(html.charCodeAt(j))
    if (c === '>') return j + 1
    switch (state) {
      case 'beforeName':
        // `=` here starts a NAME (a parse error), it is not a separator.
        if (!space && c !== '/') state = 'name'
        break
      case 'name':
        if (space) state = 'afterName'
        else if (c === '/') state = 'beforeName'
        else if (c === '=') state = 'beforeValue'
        break
      case 'afterName':
        if (c === '/') state = 'beforeName'
        else if (c === '=') state = 'beforeValue'
        else if (!space) state = 'name'
        break
      case 'beforeValue':
        if (c === '"' || c === "'") {
          const close = html.indexOf(c, j + 1)
          if (close === -1) return -1
          j = close // after a quoted value the tokenizer expects a new attribute
          state = 'beforeName'
        } else if (!space) {
          state = 'unquoted'
        }
        break
      case 'unquoted':
        if (space) state = 'beforeName'
        break
    }
  }
  return -1
}

/** HTML's ASCII whitespace: tab, LF, FF, CR, space. */
function isSpace(code: number): boolean {
  return code === 0x09 || code === 0x0a || code === 0x0c || code === 0x0d || code === 0x20
}
